import React, { useState, useEffect } from 'react'
import ReactFlagsSelect from 'react-flags-select'
import Input from './common/Input'
import MultiSelect from './common/MultiSelect'
import { phoneGroupApi } from '../services/api'

const FormField = ({ label, type = 'text', ...props }) => (
  <Input
    label={label}
    type={type}
    {...props}
  />
)

const TextArea = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <textarea
      className="w-full px-4 py-2.5 bg-white rounded-xl shadow-[0_2px_8px_rgb(0,0,0,0.08)] focus:shadow-[0_2px_8px_rgb(0,0,0,0.12)] focus:ring-0 focus:outline-none text-gray-900 min-h-[100px] text-sm"
      {...props}
    />
  </div>
)

const CountrySelect = ({ label, value, onChange, ...props }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <ReactFlagsSelect
      selected={value}
      onSelect={onChange}
      searchable
      placeholder="Select Country"
      className="!p-0"
      selectButtonClassName="!w-full !px-4 !py-2.5 !text-sm !border-0 !rounded-xl !shadow-[0_2px_8px_rgb(0,0,0,0.08)] focus:!shadow-[0_2px_8px_rgb(0,0,0,0.12)] !bg-white"
      {...props}
    />
  </div>
)

const WebsiteConfigForm = ({ onSubmit, onCancel, initialValues = {} }) => {
  const [selectedCountry, setSelectedCountry] = useState(initialValues.country || '')
  const [phoneGroups, setPhoneGroups] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedPhones, setSelectedPhones] = useState(() => {
    if (initialValues.whatsapp_group_ids) {
      return String(initialValues.whatsapp_group_ids).split(',').map(s => s.trim());
    }
    return [];
  });

  useEffect(() => {
    const fetchPhoneGroups = async () => {
      try {
        const res = await phoneGroupApi.list({})
        if (res.code === 1 && res.data) {
          // The backend returns the list of groups in res.data
          const options = res.data.map(group => ({
            label: group.owner_name,
            value: String(group.id),
            owner_name: group.owner_name
          }))
          setPhoneGroups(options)
          
          setSelectedPhones(prev => {
            // 获取到选项后，进一步过滤已经选中的值，剔除不再选项中的历史脏数据
            const validValues = options.map(o => o.value);
            return prev.filter(v => validValues.includes(v));
          });
        }
      } catch (error) {
        console.error('Failed to fetch phone groups:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPhoneGroups()
  }, [initialValues.whatsapp_group_ids, initialValues.whatsapp_phone])

  const handleSubmit = (e) => {
    e.preventDefault()
    // 移除 Phone Groups 必填校验，使其变成选填
    const formData = new FormData(e.target)
    const data = Object.fromEntries(formData.entries())
    data.country = selectedCountry
    
    data.whatsapp_group_ids = selectedPhones.join(',');
    
    onSubmit(data)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen sm:min-h-0 bg-white sm:rounded-xl p-4 sm:p-6 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-primary border-t-transparent"></div>
          <p className="mt-4 text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen sm:min-h-0 bg-white sm:rounded-xl p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Add Configuration</h2>
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <FormField
              name="domain"
              label="Domain"
              placeholder="Enter domain name"
              defaultValue={initialValues.domain}
              required
            />

            <CountrySelect
              label="Country"
              value={selectedCountry}
              onChange={setSelectedCountry}
              required
            />

            <FormField
              name="pixel"
              label="Pixel"
              placeholder="Enter pixel code"
              defaultValue={initialValues.pixel}
              required
            />

            <FormField
              name="whatsapp_phone"
              label="Whatsapp Phone"
              placeholder="Enter whatsapp phone"
              defaultValue={initialValues.whatsapp_phone}
            />

            <FormField
              name="conversion_token"
              label="Conversion Token"
              placeholder="Enter conversion token"
              defaultValue={initialValues.conversion_token}
            />

            <MultiSelect
              label="Phone Groups"
              options={phoneGroups}
              value={selectedPhones}
              onChange={setSelectedPhones}
              placeholder="Select phone groups"
            />

            <FormField
              name="telegram_group_id"
              label="Telegram Group ID"
              placeholder="Enter telegram group ID"
              defaultValue={initialValues.telegram_group_id}
            />
          </div>

          <TextArea
            name="remarks"
            label="Remarks"
            placeholder="Enter any additional notes"
            defaultValue={initialValues.remarks}
          />

          <div className="flex flex-col-reverse sm:flex-row justify-end space-y-4 space-y-reverse sm:space-y-0 sm:space-x-4 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default WebsiteConfigForm 